import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  useGetUserCreatorByTagQuery,
} from "../slices/usersApiSlice";
import { useGetProductsByUserIdQuery } from "../slices/productsApiSlice";
import Loader from "../components/Loader";
import Message from "../components/Message";
import Paginate from "../components/Paginate";
import { FaBrush, FaInstagram, FaPinterest, FaFacebook, FaTiktok, FaStar } from "react-icons/fa";
import Product from "../components/Product";
import SearchHelper from "../components/SearchHelper";
import { skipToken } from "@reduxjs/toolkit/query";
import SearchBoxCreator from "../components/SearchBoxCreator";
import { useSelector } from "react-redux";
import UserAvatar from "../components/UserAvatar.jsx";

function CreatorScreen() {
  const { tag: creatorTag, pageNumber, keyword, age, category, sort } = useParams();
  const location = useLocation();
  const [selectedCreatorCategory, setSelectedCreatorCategory] = useState("all");
  const [bannerImageError, setBannerImageError] = useState(false);

  const { data: creatorData, isLoading, error } =
    useGetUserCreatorByTagQuery(creatorTag);

  const { userInfo } = useSelector((state) => state.auth);
  const { data: productData, isLoading: isProductLoading, error: productError } = useGetProductsByUserIdQuery(
    creatorData?.userId
      ? {
          userId: creatorData.userId,
          pageNumber,
          keyword,
          category,
          age,
          sort,
          creatorCategoryId: selectedCreatorCategory,
        }
      : skipToken
  );

  useEffect(() => {
    setSelectedCreatorCategory("all");
    setBannerImageError(false);
    window.scrollTo(0, 0);
  }, [creatorTag]);

  useEffect(() => {
    if (location.hash !== "#creator-products" || isProductLoading || !productData) return;
    const frame = requestAnimationFrame(() => {
      const grid = document.getElementById("creator-products");
      if (!grid) return;
      const headerHeight = document.querySelector(".site-header")?.getBoundingClientRect().height || 0;
      window.scrollTo({ top: Math.max(0, grid.getBoundingClientRect().top + window.scrollY - headerHeight - 12), behavior: "auto" });
    });
    return () => cancelAnimationFrame(frame);
  }, [location.key, location.hash, isProductLoading, productData]);

  const navigate = useNavigate();
  const handleGoToCreatorPage = () => {
    navigate("/profile/creator");
  };

  const formatInitials = (name = "") => {
    const parts = String(name)
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 0) return "CR";
    const first = parts[0][0]?.toUpperCase() || "C";
    const second = parts.length > 1 ? parts[parts.length - 1][0]?.toUpperCase() || "" : "";
    return `${first}${second}`;
  };

  const bannerInitials = formatInitials(creatorData?.userCreator?.name);
  const bannerPatternSvg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="120" viewBox="0 0 160 120"><text x="18" y="76" font-family="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial" font-size="40" font-weight="700" fill="#0f172a" fill-opacity="0.1" transform="rotate(-16 80 60)">${bannerInitials}</text></svg>`
  );

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || error.error}</Message>;

  return (
    <div className="creator-public-page min-w-0 space-y-4 [overflow-wrap:anywhere]">
      {userInfo && creatorData?.userId === userInfo._id && (
        <div>
          <button
            type="button"
            onClick={handleGoToCreatorPage}
            className="inline-flex items-center rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700"
          >
            <FaBrush className="mr-2" />
            Go to Creator Edit Page
          </button>
        </div>
      )}

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="w-full min-w-0 bg-slate-100">
          {creatorData?.userCreator?.bannerImage && !bannerImageError ? (
            <img
              src={creatorData.userCreator.bannerImage}
              alt=""
              className="block h-auto w-full max-w-full"
              onError={() => setBannerImageError(true)}
            />
          ) : (
            <div
              className="relative h-36 w-full overflow-hidden sm:h-52"
              style={{
                backgroundImage: `linear-gradient(120deg, #eaf5ff 0%, #f4f8ff 45%, #edf8f4 100%), url("data:image/svg+xml,${bannerPatternSvg}")`,
                backgroundRepeat: "no-repeat, repeat",
                backgroundSize: "cover, 160px 120px",
              }}
            >
              <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-slate-500">
                No banner uploaded yet
              </div>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 p-3 sm:p-4 lg:grid-cols-12">
          <div className="min-w-0 space-y-3 lg:col-span-4">
            <div>
              <UserAvatar
                user={{
                  name: creatorData.userCreator.name,
                  profileImage: creatorData.userCreator.profileImage,
                }}
                size={120}
              />
            </div>
            <div>
              <h1 className="mb-1 text-2xl font-semibold text-slate-900">
                {creatorData.userCreator.name}
              </h1>
              <p className="mb-1 flex items-center gap-1 text-sm text-slate-600">
                <FaStar className="text-amber-400" />
                <span>
                  {(creatorData?.creatorReviewStats?.averageRating ?? 0).toFixed(1)} (
                  {creatorData?.creatorReviewStats?.totalReviews ?? 0} reviews)
                </span>
              </p>
            </div>

            <div className="flex flex-wrap gap-2 text-sm">
              {creatorData?.userCreator?.handlers?.instagram?.isEnabled && (
                <a
                  href={`https://instagram.com/${creatorData.userCreator.handlers.instagram.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 no-underline hover:bg-slate-50"
                >
                  <FaInstagram /> Instagram
                </a>
              )}
              {creatorData?.userCreator?.handlers?.pinterest?.isEnabled && (
                <a
                  href={`https://pinterest.com/${creatorData.userCreator.handlers.pinterest.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 no-underline hover:bg-slate-50"
                >
                  <FaPinterest /> Pinterest
                </a>
              )}
              {creatorData?.userCreator?.handlers?.facebook?.isEnabled && (
                <a
                  href={`https://facebook.com/${creatorData.userCreator.handlers.facebook.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 no-underline hover:bg-slate-50"
                >
                  <FaFacebook /> Facebook
                </a>
              )}
              {creatorData?.userCreator?.handlers?.tiktok?.isEnabled && (
                <a
                  href={`https://tiktok.com/@${creatorData.userCreator.handlers.tiktok.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-slate-700 no-underline hover:bg-slate-50"
                >
                  <FaTiktok /> TikTok
                </a>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 min-w-0 p-3 text-sm leading-6 text-slate-700 lg:col-span-8">
            {creatorData.userCreator.description}
          </div>
        </div>
      </section>

      <section className="space-y-4">
        {isProductLoading ? (
          <Loader />
        ) : productError ? (
          <Message variant="danger">
            {productError?.productData?.message || productError.error}
          </Message>
        ) : (
          <>
            <div className="min-w-0 rounded-2xl bg-white p-0">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                    Creator Catalog
                  </p>
                  {keyword ? (
                    <h2 className="text-xl font-semibold text-slate-900">Results for "{keyword}"</h2>
                  ) : (
                    <h2 className="text-xl font-semibold text-slate-900">All Resources</h2>
                  )}
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Browse this creator&apos;s published resources by search, sort, and category.
                  </p>
                </div>
                <div className="w-full min-w-0 xl:max-w-[520px]">
                  <SearchBoxCreator />
                </div>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4">
                <SearchHelper compact />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 lg:grid-cols-12">
                <aside className="min-w-0 lg:col-span-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <h3 className="mb-3 text-base font-semibold text-slate-900">Categories</h3>
                    <button
                      type="button"
                      onClick={() => setSelectedCreatorCategory("all")}
                      className={`mb-2 w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold leading-5 transition ${
                        selectedCreatorCategory === "all"
                          ? "bg-sky-600 text-white"
                          : "bg-white text-slate-700 hover:bg-slate-100"
                      }`}
                    >
                      <span className="block break-words whitespace-normal">
                        All Resources ({creatorData?.allResourcesCount ?? 0})
                      </span>
                    </button>
                    {(creatorData?.creatorProductCategories || []).map((entry) => (
                      <button
                        key={entry._id}
                        type="button"
                        onClick={() => setSelectedCreatorCategory(String(entry._id))}
                        className={`mb-2 w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold leading-5 transition ${
                          selectedCreatorCategory === String(entry._id)
                            ? "bg-sky-600 text-white"
                            : "bg-white text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <span className="block break-words whitespace-normal">
                          {entry.name} ({entry.count})
                        </span>
                      </button>
                    ))}
                  </div>
                </aside>

                <div id="creator-products" className="min-w-0 lg:col-span-9">
                  {productData?.products?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                      {productData.products.map((product) => (
                        <Product key={product._id} product={product} className="min-w-0 !my-0" />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center">
                      <h3 className="text-lg font-semibold text-slate-900">No items were found</h3>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <Paginate
              pages={productData?.pages || 1}
              page={productData?.page || 1}
              keyword={keyword ? keyword : ""}
              age={age ? age : ""}
              category={category ? category : ""}
              sort={sort ? sort : ""}
              tag={creatorTag ? creatorTag : ""}
            />
          </>
        )}
      </section>
    </div>
  );
}

export default CreatorScreen;

